import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Clock, ArrowUpRight, Send } from 'lucide-react';
import Seo from '../components/Seo';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import { BLOG_POSTS, BLOG_CATEGORIES } from '../mock';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';

const PostCard = ({ p, featured = false }) => (
  <Link to={`/blog/${p.slug}`} className={`group block rounded-3xl overflow-hidden border border-slate-100 bg-white hover:shadow-2xl transition-all ${featured ? 'lg:col-span-2 lg:row-span-2' : ''}`}>
    <div className={`relative bg-gradient-to-br ${p.gradient} ${featured ? 'aspect-[16/10]' : 'aspect-[16/9]'} overflow-hidden`}>
      <div className="absolute inset-0 bg-black/10" />
      <div className="absolute inset-0 bg-grid opacity-10" />
      <span className="absolute top-4 left-4 bg-white/90 backdrop-blur text-[#0a0e27] text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
        {p.category}
      </span>
      <div className="absolute bottom-4 right-4 opacity-30 text-white text-8xl font-black leading-none">#{p.id}</div>
    </div>
    <div className="p-6">
      <h3 className={`font-extrabold text-[#0a0e27] group-hover:text-[#FF5B24] transition-colors leading-tight ${featured ? 'text-2xl md:text-3xl' : 'text-lg'}`}>
        {p.title}
      </h3>
      <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">{p.excerpt}</p>
      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-[10px] font-bold text-slate-600">
            {p.author.charAt(0)}
          </div>
          <span className="font-medium text-[#0a0e27]">{p.author}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Clock size={12} /> {p.readTime}
        </div>
      </div>
    </div>
  </Link>
);

const Blog = () => {
  const [cat, setCat] = useState('Todos');
  const [q, setQ] = useState('');
  const filtered = BLOG_POSTS.filter((p) =>
    (cat === 'Todos' || p.category === cat) &&
    (q === '' || (p.title + p.excerpt).toLowerCase().includes(q.toLowerCase()))
  );

  const submitNewsletter = (e) => {
    e.preventDefault();
    e.target.reset();
    toast({ title: 'Inscrição realizada!', description: 'Você receberá nossos melhores conteúdos no e-mail.' });
  };

  return (
    <>
      <Seo
        title="Blog"
        description="Insights, guias e novidades sobre atendimento ao cliente, inteligência artificial, vendas e operação — direto do time da CoreDesk."
        path="/blog"
      />
      <Header />
      <PageHero
        eyebrow="Blog CoreDesk"
        title="Insights, guias e |novidades"
        subtitle="Conteúdo prático sobre atendimento ao cliente, inteligência artificial, vendas e operação."
      />

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="flex flex-wrap gap-2">
              {BLOG_CATEGORIES.map((c) => (
                <button key={c} onClick={() => setCat(c)} className={`px-4 py-2 rounded-full text-sm font-semibold transition ${cat === c ? 'bg-[#0a0e27] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                  {c}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar no blog..." className="h-11 pl-10 rounded-full" />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500">Nenhum artigo encontrado.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => <PostCard key={p.id} p={p} featured={i === 0} />)}
            </div>
          )}
        </div>
      </section>

      <section className="py-20 bg-[#0a0e27] relative overflow-hidden">
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-[#FF5B24]/25 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-white">
            Receba conteúdos exclusivos <br/><span className="text-[#FF5B24]">toda semana</span>
          </h2>
          <p className="mt-4 text-white/70">Guias, cases e novidades sobre atendimento e IA. Sem spam, cancele quando quiser.</p>
          <form onSubmit={submitNewsletter} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <Input required type="email" placeholder="seu@email.com" className="h-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-full px-5" />
            <Button type="submit" className="btn-coral h-12 px-6 rounded-full font-semibold gap-1.5">
              Assinar <Send size={15} />
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default Blog;
